import { CheckIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { InlineError } from '../book/components/BookingShell.component';
import { HoneypotField } from '../login/HoneypotField.component';
import { RULES } from '../login/password_rules';
import { TermsOfServiceText } from '../login/TermsOfService.component';
import { GetStartedValues } from './get_started_rules';

export interface AccountOutcome {
    ok: boolean;
    reason?: 'exists' | 'duplicate_brand' | 'invalid' | 'failed';
    message?: string;
}

// #region AccountStep
/**
 * The account door, opened in place: they've already told us who they are, so all that's left is a
 * password and the terms. One button saves their answers and creates the account.
 */
export const AccountStep: React.FunctionComponent<{
    values: GetStartedValues;
    onCreate: (password: string, skipDuplicateCheck: boolean, honeypot: string) => Promise<AccountOutcome>;
    onEditDetails: () => void;
}> = ({ values, onCreate, onEditDetails }): React.ReactElement => {
    const [password, setPassword] = React.useState('');
    const [reveal, setReveal] = React.useState(false);
    const [terms, setTerms] = React.useState(false);
    const [showTerms, setShowTerms] = React.useState(false);
    const [tried, setTried] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<AccountOutcome | null>(null);
    const [honeypot, setHoneypot] = React.useState('');
    const strong = RULES.every(([, ok]) => ok(password));

    const create = async (skipDuplicateCheck = false) => {
        setTried(true);
        if (!strong || !terms) return;
        setSaving(true); setError(null);
        const outcome = await onCreate(password, skipDuplicateCheck, honeypot);
        setSaving(false);
        if (!outcome.ok) setError(outcome);
    };

    const initials = `${values.first_name.trim()[0] ?? ''}${values.last_name.trim()[0] ?? ''}`.toUpperCase();

    return (
        <form className="flex flex-col gap-y-4 rounded-2xl border border-emerald-600/40 bg-emerald-600/5 p-4" noValidate onSubmit={(ev) => { ev.preventDefault(); create(); }}>
            <div className="flex items-center gap-x-3">
                <span aria-hidden className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-semibold text-white">{initials}</span>
                <div className="min-w-0 flex-1 text-sm">
                    <div className="truncate font-medium">{values.first_name.trim()} {values.last_name.trim()} · {values.company_name.trim()}</div>
                    <div className="truncate text-zinc-500 dark:text-zinc-400">{values.email.trim()}</div>
                </div>
                <button type="button" onClick={onEditDetails} className="text-xs font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100">Edit</button>
            </div>

            <HoneypotField value={honeypot} onChange={setHoneypot} />

            <div className="flex flex-col gap-y-2">
                {/* Lets password managers file the new password under the right account. */}
                <input type="email" name="username" autoComplete="username" value={values.email.trim()} readOnly hidden />
                <label htmlFor="gs-password" className="text-sm font-medium">Choose a password</label>
                <div className="relative">
                    <input id="gs-password" type={reveal ? 'text' : 'password'} value={password} autoComplete="new-password" maxLength={64} autoFocus
                        aria-invalid={tried && !strong} aria-describedby="gs-rules" onChange={(ev) => setPassword(ev.target.value)}
                        className={`h-11 w-full rounded-xl border bg-surface px-3 pr-10 text-sm outline-none transition focus:ring-2 focus:ring-emerald-600 ${tried && !strong ? 'border-red-400' : 'border-surface-border'}`} />
                    <button type="button" onClick={() => setReveal(!reveal)} aria-label={reveal ? 'Hide password' : 'Show password'}
                        className="absolute inset-y-0 right-2 my-auto h-7 w-7 rounded-md p-1 text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200">
                        {reveal ? <EyeSlashIcon /> : <EyeIcon />}
                    </button>
                </div>
                <ul id="gs-rules" className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:grid-cols-3">
                    {RULES.map(([label, ok]) => {
                        const met = ok(password);
                        return <li key={label} className={`flex items-center gap-1 ${met ? 'text-emerald-700 dark:text-emerald-400' : tried ? 'text-red-600 dark:text-red-400' : 'text-zinc-500'}`}><CheckIcon className={`h-3.5 w-3.5 ${met ? '' : 'opacity-30'}`} strokeWidth={2.5} />{label}</li>;
                    })}
                </ul>
            </div>

            <div className="flex flex-col gap-y-2">
                <label className="flex items-start gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                    <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-surface-border accent-emerald-600" checked={terms} onChange={(ev) => setTerms(ev.target.checked)} />
                    <span>I agree to the <button type="button" className="font-medium text-emerald-700 hover:underline dark:text-emerald-400" aria-expanded={showTerms} onClick={() => setShowTerms(!showTerms)}>Terms of Service</button></span>
                </label>
                {tried && !terms ? <span role="alert" className="text-xs text-red-600 dark:text-red-400">Please accept the terms to continue.</span> : null}
                {showTerms ? <div className="max-h-64 overflow-y-auto rounded-xl border border-surface-border bg-surface text-sm"><TermsOfServiceText compact /></div> : null}
            </div>

            {error ? (
                <div className="flex flex-col gap-y-2">
                    <InlineError message={error.reason === 'duplicate_brand' ? `${error.message} If you work with them, ask their admin to invite you instead.` : error.message ?? 'Something went wrong. Please try again.'} />
                    {error.reason === 'exists' ? <a href="/login" target="_blank" rel="noopener" className="text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400">Sign in instead</a> : null}
                    {error.reason === 'duplicate_brand' ? <button type="button" onClick={() => create(true)} className="self-start text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400">It&apos;s a different brand. Create my account</button> : null}
                </div>
            ) : null}

            <button type="submit" disabled={saving} className="inline-flex h-11 items-center justify-center rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white transition hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 disabled:opacity-60">
                {saving ? 'Creating your account…' : 'Create my account'}
            </button>
        </form>
    );
};
